import {
    ClipboardCheck,
    Download,
    Images,
    type LucideIcon,
    Pencil,
    Settings,
    Target,
} from 'lucide-react';
import type React from 'react';
import type { IconType } from 'react-icons';
import { RiImageAiLine } from 'react-icons/ri';
import ContouringContent from '../WorkflowSidebar/ContouringStep';
import PredictivePlanningContent from '../WorkflowSidebar/DoseStep';
import ExportContent from '../WorkflowSidebar/ExportStep';
import RegistrationContent from '../WorkflowSidebar/RegistrationStep';
import ReviewContent from '../WorkflowSidebar/ReviewStep';
import SetupContent from '../WorkflowSidebar/SetupStep';
import SyntheticImagingContent from '../WorkflowSidebar/SyntheticImagingStep';

/**
 * Data prerequisites that a workflow step may require.
 * Each maps to a specific i18n disabled-reason message.
 */
export type StepRequirement = 'image' | 'secondaryImages' | 'structureSet';

export const STEP_REQUIREMENT_REASON_ID: Record<StepRequirement, string> = {
    image: 'workflow.step.disabled.noImage',
    secondaryImages: 'workflow.step.disabled.noSecondaryImages',
    structureSet: 'workflow.step.disabled.noStructureSet',
};

export interface WorkflowStep {
    id: string;
    labelId: string;
    path: string;
    icon: LucideIcon | IconType;
    content: React.ComponentType;
    /** Data that must be loaded before this step is available. */
    requires?: StepRequirement[];
}

export const WORKFLOW_STEPS: WorkflowStep[] = [
    {
        id: 'setup',
        labelId: 'workflow.setup',
        path: '/setup',
        icon: Settings,
        content: SetupContent,
    },
    {
        id: 'synthetic-imaging',
        labelId: 'workflow.syntheticImaging',
        path: '/synthetic-imaging',
        icon: RiImageAiLine,
        content: SyntheticImagingContent,
        requires: ['image'],
    },
    {
        id: 'registration',
        labelId: 'workflow.imageRegistration',
        path: '/image-registration',
        icon: Images,
        content: RegistrationContent,
        requires: ['image'],
    },
    {
        id: 'contouring',
        labelId: 'workflow.contouring',
        path: '/contouring',
        icon: Pencil,
        content: ContouringContent,
        requires: ['image'],
    },
    {
        id: 'review',
        labelId: 'workflow.review',
        path: '/review',
        icon: ClipboardCheck,
        content: ReviewContent,
        requires: ['structureSet'],
    },
    {
        id: 'predictive-planning',
        labelId: 'workflow.dose',
        path: '/predictive-planning',
        icon: Target,
        content: PredictivePlanningContent,
        requires: ['structureSet'],
    },
    {
        id: 'export',
        labelId: 'workflow.export',
        path: '/export',
        icon: Download,
        content: ExportContent,
        requires: ['image'],
    },
];
